import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import JEVIScraperLogo from '@/assets/JEVIScraper_NoBG.png';

function LoginShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#1e2d4a] via-[#2e436a] to-[#3d5280]">
      <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#cd212a]/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-[#cd212a]/20 blur-3xl" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-64 w-64 -translate-x-1/2 rounded-full bg-white/5 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />
      <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
        {children}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <LoginShell>
      <div className="text-center">
        <div className="mx-auto mb-6 h-16 w-16 animate-spin rounded-full border-[3px] border-white/20 border-t-white" />
        <p className="text-sm font-medium tracking-wide text-white/80">
          Verifica autenticazione...
        </p>
      </div>
    </LoginShell>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, loading } = useAuth();
  const error = searchParams.get('error');

  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/home');
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading && !error) {
    return <LoadingState />;
  }

  const errorMessages: Record<string, string> = {
    account_disabled: 'Il tuo account JEVIS risulta disattivato.',
    access_denied:
      'Il tuo profilo JEVIS non risulta nell\'area Business Solutions (BS). Accesso consentito solo agli utenti area BS.',
    user_sync_failed: 'Impossibile sincronizzare il profilo locale.',
    invalid_code: 'Codice di accesso non valido o scaduto. Riprova ad accedere.',
    invalid_token: 'Sessione non valida o scaduta. Riprova ad accedere.',
    kernel_api_error:
      'Il gateway JEVIS (api.jevis.it) non risponde correttamente alla validazione del token. Contatta il team Kernel o riprova più tardi.',
  };

  return (
    <LoginShell>
      <div className="w-full max-w-md">
        <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/95 shadow-2xl shadow-black/20 backdrop-blur-xl">
          <div className="relative bg-gradient-to-br from-[#2e436a] to-[#1e2d4a] px-8 pb-10 pt-8 text-center">
            <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#cd212a]/25 blur-2xl" />
            <div className="relative mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-lg ring-2 ring-white/20">
              <img
                src={JEVIScraperLogo}
                alt="JEVIScraper"
                className="h-14 w-14 object-contain"
              />
            </div>
            <h1 className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-2xl font-extrabold tracking-tight text-transparent">
              JEVIS Business Analyzer
            </h1>
            <p className="mt-2 text-sm text-white/65">
              Gestione lead e task settimanali
            </p>
          </div>

          <div className="px-8 py-8">
            <div className="mb-6 text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-secondary/50">
                Benvenuto
              </p>
              <h2 className="mt-1 text-xl font-bold text-secondary">Accedi al tuo workspace</h2>
              <p className="mt-2 text-sm leading-relaxed text-gray-500">
                Usa il tuo account aziendale JEVIS per continuare in modo sicuro.
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="mb-6 flex gap-3 rounded-xl border border-red-200/80 bg-red-50 px-4 py-3 text-left"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                  !
                </span>
                <p className="text-sm text-red-800">
                  {errorMessages[error] ?? 'Errore durante il login.'}
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={login}
              className="group relative w-full overflow-hidden rounded-xl bg-primary px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#b81d25] hover:shadow-xl hover:shadow-primary/30 active:translate-y-0"
            >
              <span className="relative flex items-center justify-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15">
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 3h4a2 2 0 012 2v4M10 14L21 3M10 14v7a2 2 0 002 2h7"
                    />
                  </svg>
                </span>
                Accedi con JEVIS
              </span>
            </button>

            <p className="mt-6 text-center text-xs leading-relaxed text-gray-400">
              Accesso riservato agli account{' '}
              <span className="font-medium text-secondary/70">@jevis.it</span>
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-white/40">
          JevisScraper · piattaforma interna JEVIS
        </p>
      </div>
    </LoginShell>
  );
}
